import {UI, registerStyle, styleRule, StyleSheet} from "../../../stem-core/src/ui/UI";

import {Router} from "../../../stem-core/src/ui/Router";
import {Messages} from "../../Messages";
import {
    WIDGET_CARD_PADDING_BOTTOM,
    WIDGET_CARD_PADDING_TOP,
} from "../WidgetConstants";
import {isSmallScreen, isKeyboardExpanded} from "../../Utils";
import {Device} from "../../../stem-core/src/base/Device";
import {Dispatcher} from "../../../stem-core/src/base/Dispatcher";
import {iFrameConnection} from "../../services/IFrameConnection";
import {IFrameMessages} from "../../../blink-sdk/messaging/IFrameMessages";
import {DEFAULT_TRANSITION_TIME_MS} from "../../../blink-sdk/Constants";
import {WidgetWalletTheme} from "../WidgetTheme.js";


export class WalletCardStyle extends StyleSheet {
    @styleRule
    container = {
        zIndex: 1,
        width: "100%",
        padding: `${WIDGET_CARD_PADDING_TOP}px 0 ${WIDGET_CARD_PADDING_BOTTOM}px`,
        display: "flex",
        flexDirection: "column",
        height: this.themeProps.FIT_CONTENT,
        minHeight: this.themeProps.FIT_CONTENT,
        justifyContent: "flex-end",
        alignSelf: "flex-end",
        borderRadius: () => isSmallScreen() && !Device.isLandscape() ? 0 : "12px 12px 0 0",
    };

    @styleRule
    secondaryButton = {
        userSelect: "none",
        margin: "6px auto 0",
        padding: "6px 18px",
        width: this.themeProps.FIT_CONTENT,
        cursor: "pointer",
        transition: ".2s ease",
        color: this.themeProps.WALLET_BUTTON_SECONDARY_COLOR,
        fontSize: 14,
        opacity: 0.8,
        ":hover": {
            color: this.themeProps.WALLET_BUTTON_SECONDARY_HOVER_COLOR,
            opacity: 1,
        },
    };

    @styleRule
    title = {
        fontSize: 21,
        fontWeight: 200,
        margin: "0px 0px 24px 24px",
        color: this.themeProps.WALLET_CARD_TITLE,
    };

    @styleRule
    content = {
        zIndex: 2,
        padding: "0 24px",
        overflow: ["overlay", "auto"],
    };

    @styleRule
    buttonContainer = {
        padding: "0 24px",
        marginTop: "18px",
        ">:first-child": {
            width: "100%",
            textAlign: "center",
        },
    };

    @styleRule
    contentSection = {
        display: "flex",
        flexDirection: "column",
        position: "relative",
        justifyContent: "flex-end",
    };

    @styleRule
    hidden = {
        visibility: "hidden",
    };

    scrollFade = {
        boxShadow: this.themeProps.MERCHANT_LOGO_BOTTOM_SHADOW,
        backgroundColor: "transparent",
        height: 8,
        zIndex: 10,
        position: "relative",
        width: "100%",
    };

    @styleRule
    scrollTopFade = {
        ...this.scrollFade,
        marginTop: -8,
    };

    @styleRule
    scrollBottomFade = {
        ...this.scrollFade,
        transform: "rotate(180deg)",
        marginBottom: -8,
    };

    @styleRule
    keyboardExpanded = {
        ">:nth-child(3)": {
            overflow: "initial",
            height: "fit-content",
        },
        overflow: "auto !important",
        display: "initial",
    };

    @styleRule
    noScroll = {
        overflow: "hidden",
    };
}

// TODO @cleanup rename to WidgetPanel
@registerStyle(WalletCardStyle)
export class WalletCard extends UI.Element {
    scrolledFromTop = false;
    scrolledFromBottom = false;
    hideScroll = false;

    getDefaultOptions() {
        return {
            theme: WidgetWalletTheme, // Applies to all Blink wallet cards
        }
    }

    checkForScrollFadeDisplay() {
        const {hidden} = this.styleSheet;

        if (isKeyboardExpanded()) {
            this.scrolledFromBottom = false;
            this.scrolledFromTop = false;
            this.topFade.addClass(hidden);
            this.bottomFade.addClass(hidden);
            return;
        }

        const {scrollTop, scrollHeight, offsetHeight} = this.content.node;

        if (scrollTop && offsetHeight) {
            this.scrolledFromTop = true;
            this.topFade.removeClass(hidden);
        } else {
            this.scrolledFromTop = false;
            this.topFade.addClass(hidden);
        }

        if (scrollHeight - offsetHeight - scrollTop > 10 && offsetHeight) {
            this.scrolledFromBottom = true;
            this.bottomFade.removeClass(hidden);
        } else {
            this.scrolledFromBottom = false;
            this.bottomFade.addClass(hidden);
        }
    }

    getChildrenToRender() {
        const {styleSheet} = this;
        const contentClass = styleSheet.contentSection + (isKeyboardExpanded() ? styleSheet.keyboardExpanded : "")

        return [
            <div ref="contentSection" className={contentClass}>
                {this.renderHeaderSection()}
                <div ref="topFade" className={styleSheet.scrollTopFade + (this.scrolledFromTop ? "" : styleSheet.hidden)} />
                <div ref="content" className={styleSheet.content + (this.hideScroll ? styleSheet.noScroll : "")}>
                    {this.renderContent()}
                </div>
                <div ref="bottomFade" className={styleSheet.scrollBottomFade + (this.scrolledFromBottom ? "" : styleSheet.hidden)} />
                <div className={styleSheet.buttonContainer}>
                    {this.renderPrimaryButton()}
                </div>
            </div>,
            this.renderSecondaryButton()
        ];
    }

    renderHeaderSection() {
        let {title, getTitle} = this.options;

        if (typeof getTitle === "function") {
            title = getTitle();
        }

        return title ? <div className={this.styleSheet.title}>{title}</div> : null;
    }

    renderContent() {
        return this.render();
    }

    renderPrimaryButton() {}

    renderSecondaryButton() {
        const handleButtonClick = (event) => {
            event.preventDefault(); // TODO @cleanup I don't think there's a reason for this
            event.stopPropagation();
            Router.back();
        };

        return (
            <div onClick={handleButtonClick} className={this.styleSheet.secondaryButton}>
                {Messages.cancel}
            </div>
        )
    }

    computeHeight() {
        return this.getHeight();
    }

    onMount() {
        super.onMount();

        this.content.addNodeListener("scroll", () => this.checkForScrollFadeDisplay());
        // Timeout needs to be increased due to possible rendering lag.
        this.attachTimeout(() => this.checkForScrollFadeDisplay(), DEFAULT_TRANSITION_TIME_MS + 200);
        this.attachListener(iFrameConnection, IFrameMessages.REPOSITION_WIDGET, () => {
            this.redraw();
            this.checkForScrollFadeDisplay();
        });

        const hideContentScrollWhileInTransition = () => {
            if (!this.content) {
                return;
            }
            this.hideScroll = true;
            this.content.setStyle("overflow", "hidden");
            this.attachTimeout(() => {
                this.content.setStyle("overflow", "overlay");
                this.hideScroll = false;
            }, DEFAULT_TRANSITION_TIME_MS);
        };

        this.attachListener(Dispatcher.Global, "preventCardScrollDisplay", hideContentScrollWhileInTransition);
        hideContentScrollWhileInTransition();
    }
}
